import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useSession } from '@/auth/SessionContext';
import { AppHeaderActions } from '@/components/AppHeaderActions';
import { ConversationChannelIcon } from '@/components/ConversationChannelIcon';
import { IconFilter, IconSearch } from '@/components/TabIcons';
import { displayVisitorLabel, isPendingHumanAttentionConversation } from '@/lib/conversationDisplay';
import { plainMessagePreview } from '@/lib/formatMessage';
import { isAbortError, pollRequestInit } from '@/lib/pollFetch';

const PAGE_SIZE = 50;

type LastPreview = {
  senderLabel: string;
  content: string;
  timestamp: string;
};

type ConvRow = {
  id: string;
  visitorName: string | null;
  externalThreadId?: string | null;
  status: string;
  controlMode: string;
  channel: string;
  updatedAt: string;
  virtualAgent: { id: string; name: string } | null;
  listMeta?: {
    lastMessagePreview: LastPreview | null;
    lastActivityAt?: string;
    contactBlacklisted?: boolean;
  };
  leadQuality?: string | null;
  leadQualityLabel?: string | null;
  leadQualityColor?: { background: string; color: string } | null;
  hasLeadQualificationPlugin?: boolean;
};

type ListResponse = { items: ConvRow[]; nextCursor: string | null };

function previewSnippet(text: string, max = 56): string {
  return plainMessagePreview(text, max);
}

function formatShortTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (sameDay) return d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('es', { day: 'numeric', month: 'short' });
}

function conversationsListPath(
  searchQ: string,
  cursor: string | null,
  bustCache = false,
  humanOnly = false,
  leadQuality = ''
): string {
  const p = new URLSearchParams({ limit: String(PAGE_SIZE) });
  const q = searchQ.trim();
  if (q) p.set('q', q);
  if (humanOnly) p.set('human', '1');
  if (leadQuality) p.set('leadQuality', leadQuality);
  if (cursor) p.set('cursor', cursor);
  if (bustCache) p.set('_', String(Date.now()));
  return `/api/console/conversations?${p.toString()}`;
}

export function ConversationListPage() {
  const { get } = useSession();
  const getRef = useRef(get);
  getRef.current = get;
  const [searchParams, setSearchParams] = useSearchParams();
  const humanOnly = searchParams.get('human') === '1';
  const leadQuality = searchParams.get('leadQuality') ?? '';
  const [leadOptions, setLeadOptions] = useState<Array<{ id: string; label: string }>>([]);

  const [rows, setRows] = useState<ConvRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const fetchGenRef = useRef(0);
  const listPollSeqRef = useRef(0);
  const nextCursorRef = useRef<string | null>(null);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    nextCursorRef.current = nextCursor;
  }, [nextCursor]);

  function toggleSearch() {
    setSearchOpen((open) => {
      if (open) {
        setSearchDraft('');
        setSearchQ('');
      }
      return !open;
    });
  }

  function toggleHumanFilter() {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (next.get('human') === '1') next.delete('human');
        else next.set('human', '1');
        return next;
      },
      { replace: true }
    );
  }

  useEffect(() => {
    if (!searchOpen) return;
    const t = window.setTimeout(() => setSearchQ(searchDraft.trim()), 350);
    return () => clearTimeout(t);
  }, [searchDraft, searchOpen]);

  /** Primera página al cambiar búsqueda */
  useEffect(() => {
    fetchGenRef.current += 1;
    const gen = fetchGenRef.current;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setRows([]);
    setNextCursor(null);
    nextCursorRef.current = null;

    const boot = window.setTimeout(() => {
      void (async () => {
        try {
          const data = await getRef.current<ListResponse>(
            conversationsListPath(searchQ, null, true, humanOnly, leadQuality)
          );
          if (cancelled || gen !== fetchGenRef.current) return;
          setRows(data.items);
          const nc = data.nextCursor ?? null;
          setNextCursor(nc);
          nextCursorRef.current = nc;
          setErr(null);
        } catch (e) {
          if (cancelled || gen !== fetchGenRef.current) return;
          setErr(e instanceof ApiError ? e.message : 'Error');
        } finally {
          if (!cancelled && gen === fetchGenRef.current) setLoading(false);
        }
      })();
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(boot);
    };
  }, [searchQ, humanOnly, leadQuality]);

  /** Refresco en vivo de la primera página (demo / otros canales) sin buscar. */
  useEffect(() => {
    if (searchOpen || searchQ.trim() !== '') return;
    let cancelled = false;
    let chainTimer: ReturnType<typeof setTimeout> | null = null;

    const mergeFirstPage = (fresh: ConvRow[], prev: ConvRow[]): ConvRow[] => {
      const freshIds = new Set(fresh.map((r) => r.id));
      const tail = prev.filter((r) => !freshIds.has(r.id));
      return [...fresh, ...tail];
    };

    const pollDelay = () =>
      document.visibilityState === 'visible' ? 2000 : 8000;

    const LIST_POLL_TIMEOUT_MS = 28_000;

    const runPoll = async (): Promise<void> => {
      if (cancelled) return;
      const seq = ++listPollSeqRef.current;
      try {
        const data = await getRef.current<ListResponse>(
          conversationsListPath('', null, true, humanOnly, leadQuality),
          pollRequestInit(LIST_POLL_TIMEOUT_MS)
        );
        if (cancelled || seq !== listPollSeqRef.current) return;
        setRows((prev) => mergeFirstPage(data.items, prev));
      } catch (e) {
        if (isAbortError(e)) return;
      }
    };

    const scheduleNext = () => {
      if (cancelled) return;
      if (chainTimer != null) {
        window.clearTimeout(chainTimer);
        chainTimer = null;
      }
      chainTimer = window.setTimeout(() => {
        chainTimer = null;
        void (async () => {
          try {
            await runPoll();
          } finally {
            if (!cancelled) scheduleNext();
          }
        })();
      }, pollDelay());
    };

    void runPoll();
    scheduleNext();

    const onVis = () => {
      if (cancelled) return;
      void runPoll();
      scheduleNext();
    };
    const onResume = () => {
      if (cancelled) return;
      void runPoll();
    };
    const onOnline = () => {
      if (cancelled) return;
      void runPoll();
      scheduleNext();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onResume);
    window.addEventListener('pageshow', onResume);
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      listPollSeqRef.current += 1;
      if (chainTimer != null) window.clearTimeout(chainTimer);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('pageshow', onResume);
      window.removeEventListener('online', onOnline);
    };
  }, [searchOpen, searchQ, humanOnly, leadQuality]);

  const loadMore = useCallback(async () => {
    const gen = fetchGenRef.current;
    const cursor = nextCursorRef.current;
    if (cursor == null || loadingMoreRef.current || loading) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const data = await getRef.current<ListResponse>(
        conversationsListPath(searchQ, cursor, false, humanOnly, leadQuality)
      );
      if (gen !== fetchGenRef.current) return;
      setRows((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        const merged = [...prev];
        for (const item of data.items) {
          if (!seen.has(item.id)) {
            seen.add(item.id);
            merged.push(item);
          }
        }
        return merged;
      });
      const nc = data.nextCursor ?? null;
      setNextCursor(nc);
      nextCursorRef.current = nc;
    } catch (e) {
      if (gen === fetchGenRef.current) {
        setErr(e instanceof ApiError ? e.message : 'Error');
      }
    } finally {
      loadingMoreRef.current = false;
      if (gen === fetchGenRef.current) setLoadingMore(false);
    }
  }, [searchQ, loading, humanOnly, leadQuality]);

  useEffect(() => {
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target || nextCursor == null || loading) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e?.isIntersecting) void loadMore();
      },
      { root, rootMargin: '160px', threshold: 0 }
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, [loadMore, nextCursor, loading, rows.length]);

  const visibleRows = useMemo(
    () => (humanOnly ? rows.filter(isPendingHumanAttentionConversation) : rows),
    [humanOnly, rows]
  );

  useEffect(() => {
    if (!humanOnly || loading || loadingMore || err) return;
    if (visibleRows.length > 0) return;
    if (nextCursor == null) return;
    void loadMore();
  }, [humanOnly, loading, loadingMore, err, visibleRows.length, nextCursor, loadMore]);

  useEffect(() => {
    let cancelled = false;
    void getRef.current<{ options: Array<{ id: string; label: string }> }>('/api/console/lead-qualification/filters')
      .then((data) => {
        if (!cancelled && Array.isArray(data.options)) setLeadOptions(data.options);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!searchOpen) return;
    const id = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [searchOpen]);

  const footerHint = useMemo(() => {
    if (loading && rows.length === 0) return 'Cargando…';
    if (loadingMore) return 'Cargando más…';
    if (!loading && visibleRows.length === 0) {
      if (humanOnly) return 'No hay conversaciones pendientes en modo humano.';
      if (searchQ.trim()) return 'Ninguna conversación coincide con la búsqueda.';
      return 'Aún no hay conversaciones.';
    }
    if (nextCursor == null && visibleRows.length > 0) return 'No hay más conversaciones';
    return null;
  }, [loading, loadingMore, nextCursor, rows.length, visibleRows.length, humanOnly, searchQ]);

  return (
    <div className="chats-list">
      <AppHeaderActions>
        <button
          type="button"
          className="app-shell__header-btn"
          aria-label={searchOpen ? 'Cerrar búsqueda' : 'Buscar conversaciones'}
          aria-pressed={searchOpen}
          onClick={() => toggleSearch()}
        >
          <IconSearch aria-hidden />
        </button>
        <button
          type="button"
          className="app-shell__header-btn app-shell__header-btn--human-filter"
          aria-label={
            humanOnly
              ? 'Filtro activo: solo pendientes en modo humano. Tocá para ver todas.'
              : 'Filtrar conversaciones pendientes en modo humano'
          }
          aria-pressed={humanOnly}
          title={
            humanOnly
              ? 'Solo pendientes en modo humano'
              : 'Ver solo las que el visitante escribió y aún no visualizaste'
          }
          onClick={() => toggleHumanFilter()}
        >
          <IconFilter aria-hidden />
        </button>
      </AppHeaderActions>
      {leadOptions.length > 0 ? (
        <div className="chats-list__search-row">
          <select
            className="chats-list__search-input"
            aria-label="Filtrar por clasificación"
            value={leadQuality}
            onChange={(e) => {
              const v = e.target.value;
              setSearchParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  if (v) next.set('leadQuality', v);
                  else next.delete('leadQuality');
                  return next;
                },
                { replace: true }
              );
            }}
          >
            <option value="">Todos los leads</option>
            <option value="unmarked">Sin marcar</option>
            {leadOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {searchOpen ?
        <div className="chats-list__search-row">
          <input
            ref={searchInputRef}
            type="search"
            enterKeyHint="search"
            className="chats-list__search-input"
            placeholder="Nombre, canal, mensaje…"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            aria-label="Buscar en conversaciones"
          />
        </div>
      : null}
      <div ref={scrollRef} className="chats-list__scroll">
        {err ? <p className="pwa-err chats-list__banner">{err}</p> : null}
        <ul className="chats-list__ul">
          {visibleRows.map((c) => {
            const preview = c.listMeta?.lastMessagePreview;
            const timeIso = preview?.timestamp ?? c.updatedAt;
            const pendingHuman = isPendingHumanAttentionConversation(c);
            return (
              <li
                key={c.id}
                className={['chats-list__row', pendingHuman ? 'chats-list__row--human-pending' : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                <Link
                  to={c.id}
                  className={['chats-list__link', pendingHuman ? 'chats-list__link--human-pending' : '']
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className="chats-list__detail">
                    <div className="chats-list__top">
                      <span className="chats-list__name">
                        <ConversationChannelIcon channel={c.channel} size={16} />
                        {displayVisitorLabel(c.visitorName, {
                          externalThreadId: c.externalThreadId,
                          channel: c.channel,
                        })}
                        {c.listMeta?.contactBlacklisted ? (
                          <span className="chats-list__blacklist-badge" title="Lista negra">
                            Lista negra
                          </span>
                        ) : null}
                        {c.hasLeadQualificationPlugin ? (
                          <span
                            className={
                              c.leadQualityColor
                                ? 'chats-list__lead-badge'
                                : c.leadQuality === 'qualified'
                                  ? 'chats-list__lead-badge chats-list__lead-badge--ok'
                                  : c.leadQuality === 'unqualified'
                                    ? 'chats-list__lead-badge chats-list__lead-badge--no'
                                    : 'chats-list__lead-badge chats-list__lead-badge--na'
                            }
                            style={
                              c.leadQualityColor
                                ? { background: c.leadQualityColor.background, color: c.leadQualityColor.color }
                                : undefined
                            }
                          >
                            {c.leadQualityLabel ||
                              (c.leadQuality === 'qualified'
                                ? 'Calificado'
                                : c.leadQuality === 'unqualified'
                                  ? 'No'
                                  : c.leadQuality
                                    ? c.leadQuality
                                    : 'Lead')}
                          </span>
                        ) : null}
                      </span>
                      <time className="chats-list__time" dateTime={timeIso}>
                        {formatShortTime(timeIso)}
                      </time>
                    </div>
                    <div className="chats-list__preview">
                      {preview ?
                        <>
                          <span className="chats-list__preview-sender">{preview.senderLabel}: </span>
                          {previewSnippet(preview.content)}
                        </>
                      : <span className="chats-list__preview-empty">Sin mensajes</span>}
                    </div>
                    <div className="chats-list__meta">
                      {c.virtualAgent?.name ?? '—'}
                      {c.controlMode === 'human' ? ' · humano' : ''}
                      {c.listMeta?.contactBlacklisted ? ' · lista negra' : ''} · {c.status}
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
        <div ref={sentinelRef} className="chats-list__sentinel" aria-hidden />
        {footerHint ? <p className="chats-list__load-more">{footerHint}</p> : null}
      </div>
    </div>
  );
}
